import { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Stack,
  Alert,
  Button,
  Group,
  Box,
  FileInput,
  SimpleGrid,
  Image,
  ActionIcon,
  Paper,
  Text,
  Loader,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCamera,
  IconPhoto,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import type { PhotoFile } from './PhotoInput';
import styles from './PhotosInputModal.module.css';

export interface PhotosInputModalProps {
  opened: boolean;
  onClose: () => void;
  /** Callback to save photos. Returns the saved photos. */
  onSave: (photos: PhotoFile[]) => Promise<void>;
  /** Callback called after successful save. */
  onSaveSuccess?: (photos: PhotoFile[]) => Promise<void>;
  photos?: PhotoFile[];
  mode: 'add' | 'edit';
  maxPhotos?: number;
}

export const PhotosInputModal = ({
  opened,
  onClose,
  onSave,
  onSaveSuccess,
  photos,
  mode,
  maxPhotos = 5,
}: PhotosInputModalProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Form state
  const [formPhotos, setFormPhotos] = useState<PhotoFile[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);

  // Camera state
  const [cameraModalOpened, setCameraModalOpened] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Refs for FormActionButtons pattern
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<PhotoFile[] | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Reset form when modal opens or photos change
  useEffect(() => {
    if (opened) {
      setInputKey((prev) => prev + 1);
      const initialPhotos = photos && photos.length > 0 ? [...photos] : [];
      setFormPhotos(initialPhotos);
      baselineRef.current = [...initialPhotos];
    }
  }, [opened, photos]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsCapturing(true);
    } catch (_error) {
      setCameraError(
        t('photos.cameraAccessError', {
          ns: 'patient-pages',
          defaultValue: 'Unable to access camera. Please check permissions.',
        })
      );
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCapturing(false);
    setCameraError(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return;
        }

        const timestamp = new Date();
        const fileName = `photo_${timestamp.getTime()}.jpg`;
        const file = new File([blob], fileName, { type: 'image/jpeg' });

        const newPhoto: PhotoFile = {
          file,
          preview: canvas.toDataURL('image/jpeg', 0.8),
          timestamp,
          source: 'camera',
        };

        const updatedPhotos = [...formPhotos, newPhoto];
        setFormPhotos(updatedPhotos);
        stopCamera();
        setCameraModalOpened(false);
      },
      'image/jpeg',
      0.8
    );
  };

  const handleCameraModalOpen = () => {
    setCameraModalOpened(true);
    startCamera();
  };

  const handleCameraModalClose = () => {
    stopCamera();
    setCameraModalOpened(false);
  };

  const handleFileUpload = (files: File[] | null) => {
    if (!files) {
      return;
    }

    const newPhotos: PhotoFile[] = [];
    let processed = 0;

    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) {
        processed++;
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        processed++;
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;

        const newPhoto: PhotoFile = {
          file,
          preview,
          timestamp: new Date(),
          source: 'upload',
        };

        newPhotos.push(newPhoto);
        processed++;

        if (processed === files.length) {
          const updatedPhotos = [...formPhotos, ...newPhotos].slice(
            0,
            maxPhotos
          );
          setFormPhotos(updatedPhotos);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index: number) => {
    const updated = formPhotos.filter((_, i) => i !== index);
    setFormPhotos(updated);
  };

  // Change detection for FormActionButtons
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }

    try {
      return !isEqual(formPhotos, baselineRef.current);
    } catch {
      return true;
    }
  };

  // Discard handler for FormActionButtons
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormPhotos([...baselineRef.current]);
    }
    setInputKey((prev) => prev + 1);
    stopCamera();
    if (cameraModalOpened) {
      setCameraModalOpened(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(formPhotos);
      // Notify parent component of successful save and wait for reload
      if (onSaveSuccess) {
        await onSaveSuccess(formPhotos);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('photos.saveError', {
        ns: 'patient-pages',
        defaultValue: 'Failed to save photos',
      }));
    } finally {
      setSaving(false);
    }
  };

  const canAddMore = formPhotos.length < maxPhotos;

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => formActionsRef.current?.handleGuardedClose()}
        closeOnClickOutside={false}
        closeOnEscape={false}
        title={
          mode === 'add'
            ? t('photos.addPhotos', {
                ns: 'patient-pages',
                defaultValue: 'Add Photos',
              })
            : t('photos.managePhotos', {
                ns: 'patient-pages',
                defaultValue: 'Manage Photos',
              })
        }
        size="lg"
      >
        <Stack gap="md">
          {/* Error Alert */}
          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title={t('photos.error', {
                ns: 'patient-pages',
                defaultValue: 'Error',
              })}
              color="red"
              onClose={() => setError(null)}
              withCloseButton
            >
              {error}
            </Alert>
          )}

          {/* Photo count */}
          <Text size="sm" fw={500}>
            {t('photos.photoCount', {
              ns: 'patient-pages',
              defaultValue: 'Photos',
            })}{' '}
            ({formPhotos.length}/{maxPhotos})
          </Text>

          {/* Action buttons */}
          <Group grow>
            <Button
              leftSection={<IconCamera size={16} />}
              variant="outline"
              onClick={handleCameraModalOpen}
              disabled={!canAddMore}
            >
              {t('examination.images.takePhoto', {
                ns: 'patient-pages',
                defaultValue: 'Take Photo',
              })}
            </Button>

            <FileInput
              placeholder={t('examination.images.uploadPhoto', {
                ns: 'patient-pages',
                defaultValue: 'Upload photos',
              })}
              accept="image/*"
              multiple
              onChange={handleFileUpload}
              disabled={!canAddMore}
            />
          </Group>

          {/* Photo grid */}
          {formPhotos.length > 0 && (
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm" key={inputKey}>
              {formPhotos.map((photo, index) => (
                <Paper
                  key={
                    photo.id || `${photo.timestamp.getTime()}-${photo.file.name}`
                  }
                  withBorder
                  p="xs"
                >
                  <Stack gap="xs">
                    <Image
                      src={photo.preview}
                      alt={t('photos.photoAlt', {
                        ns: 'patient-pages',
                        defaultValue: 'Photo {{count}}',
                        count: index + 1,
                      })}
                      height={100}
                      fit="cover"
                      radius="sm"
                    />
                    <Group justify="space-between" align="center">
                      <Text
                        size="xs"
                        c="dimmed"
                        lineClamp={1}
                        flex={1}
                      >
                        {photo.source === 'camera'
                          ? t('photos.sourceCamera', {
                              ns: 'patient-pages',
                              defaultValue: 'Camera',
                            })
                          : t('photos.sourceUpload', {
                              ns: 'patient-pages',
                              defaultValue: 'Upload',
                            })}
                      </Text>
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        size="sm"
                        onClick={() => removePhoto(index)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>
          )}

          {/* Actions */}
          <FormActionButtons
            ref={formActionsRef}
            onSave={handleSave}
            onDiscard={handleDiscardChanges}
            onClose={onClose}
            hasUnsavedChanges={hasUnsavedChanges()}
            isLoading={saving}
            saveLabel={
              mode === 'add'
                ? t('photos.addButton', {
                    ns: 'patient-pages',
                    defaultValue: 'Add',
                  })
                : t('photos.saveButton', {
                    ns: 'patient-pages',
                    defaultValue: 'Save',
                  })
            }
          />
        </Stack>
      </Modal>

      {/* Separate Camera Modal */}
      <Modal
        opened={cameraModalOpened}
        onClose={handleCameraModalClose}
        title={t('examination.images.takePhoto', {
          ns: 'patient-pages',
          defaultValue: 'Take Photo',
        })}
        size="lg"
      >
        <Stack>
          {cameraError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {cameraError}
            </Alert>
          )}

          <Box className={styles.videoContainer}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`${styles.video} ${!isCapturing ? styles.videoHidden : ''}`}
            />

            <canvas ref={canvasRef} className={styles.canvas} />
          </Box>

          {!isCapturing && (
            <Group justify="center" p="xl">
              <Loader />
            </Group>
          )}

          {isCapturing && (
            <Group justify="center">
              <Button
                onClick={capturePhoto}
                leftSection={<IconPhoto size={16} />}
                size="lg"
              >
                {t('examination.images.capturePhoto', {
                  ns: 'patient-pages',
                  defaultValue: 'Capture Photo',
                })}
              </Button>
              <Button
                variant="outline"
                onClick={handleCameraModalClose}
                size="lg"
              >
                {t('examination.images.cancel', {
                  ns: 'patient-pages',
                  defaultValue: 'Cancel',
                })}
              </Button>
            </Group>
          )}
        </Stack>
      </Modal>
    </>
  );
};

